import { Tag, Tooltip, Button, Space } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { displayNumbers } from '../../../../variables/numbersFunctions';

export const statistiquesGlobalesColumns = (compagniesBeneficiaires = []) => {
    const baseColumns = [
        {
            title: 'Exercice',
            dataIndex: 'exercice',
            key: 'exercice',
            width: 80,
            fixed: 'left',
            sorter: (a, b) => a.exercice - b.exercice,
            render: (value) => <Tag color="blue">{value}</Tag>
        },
        {
            title: 'Compagnie cédante',
            dataIndex: 'compagnieCedanteNom',
            key: 'compagnieCedante',
            width: 130,
            ellipsis: true,
            render: (nom, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{nom}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{record.compagnieCedanteCode}</div>
                </div>
            )
        },
        {
            title: 'Catégorie',
            dataIndex: 'categorieLibelle',
            key: 'categorie',
            width: 60,
            render: (libelle, record) => (
                <Tag color="purple">{record.categorieCode}</Tag>
            )
        },
        {
            title: 'Nbre sin.',
            dataIndex: 'nombreSinistres',
            key: 'nombreSinistres',
            width: 70,
            align: 'center',
            sorter: (a, b) => a.nombreSinistres - b.nombreSinistres,
            render: (value) => <strong>{value}</strong>
        },
        {
            title: 'SAP (Sinistre à payer)',
            dataIndex: 'montantTotalSap',
            key: 'sap',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.montantTotalSap - b.montantTotalSap,
            render: (value, record) => {
                const pourcentage = record.montantTotalEvaluation > 0
                    ? (value / record.montantTotalEvaluation) * 100
                    : 0;
                return (
                    <Tooltip title={`${pourcentage.toFixed(1)}% de l'évaluation`}>
                        <strong style={{ color: '#ff4d4f' }}>
                            {displayNumbers(value)} F
                        </strong>
                    </Tooltip>
                );
            }
        }
    ];

    // Colonnes dynamiques pour chaque compagnie bénéficiaire
    const beneficiaireColumns = compagniesBeneficiaires.map(comp => ({
        title: (
            <Tooltip title={comp.nom}>
                <div style={{ textAlign: 'center', fontSize: 11 }}>
                    <div style={{ fontWeight: 600 }}>{comp.nom}</div>
                    {/* <div style={{ color: '#888' }}>{(comp.taux || 0).toFixed(2)}%</div> */}
                </div>
            </Tooltip>
        ),
        key: `benef_${comp.code}`,
        width: 110,
        align: 'right',
        render: (_, record) => {
            const rep = record.repartitionParBeneficiaire?.find(
                r => r.beneficiaireCode === comp.code
            );
            return (
                <span style={{ color: '#722ed1', fontSize: 12 }}>
                    {displayNumbers(rep?.montantSAP || 0)} F<br /> <Tag style={{ marginLeft: 4 }} color="gray">{(rep?.tauxRepartition || 0).toFixed(2)}%</Tag>
                </span>
            );
        }
    }));

    return [...baseColumns, ...beneficiaireColumns];
};

export const detailSinistresColumns = (compagniesBeneficiaires = [], onVoirDetail) => {
    const baseColumns = [
        {
            title: 'N° Sinistre',
            dataIndex: 'numeroSinistre',
            key: 'numeroSinistre',
            width: 130,
            fixed: 'left',
            render: (value, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{value || 'N/A'}</div>
                    <Tag color="blue" style={{ fontSize: 10 }}>
                        {record.compagnieCedanteCode}
                    </Tag>
                </div>
            )
        },
        {
            title: 'Date de survenance',
            dataIndex: 'dateSinistre',
            key: 'dateSinistre',
            width: 100,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : 'N/A',
            sorter: (a, b) => dayjs(a.dateSinistre).unix() - dayjs(b.dateSinistre).unix()
        },
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'numeroPolice',
            width: 120
        },
        {
            title: 'Souscripteur',
            dataIndex: 'souscripteur',
            key: 'souscripteur',
            width: 150,
            ellipsis: true
        },
        {
            title: 'Compagnie',
            dataIndex: 'beneficiaire',
            key: 'beneficiaire',
            width: 150,
            ellipsis: true
        },
        {
            title: 'Catégorie',
            dataIndex: 'categorieCode',
            key: 'categorie',
            width: 80,
            render: (code) => <Tag color="purple">{code}</Tag>
        },
        {
            title: 'Statut',
            dataIndex: 'statutSinistre',
            key: 'statut',
            width: 90,
            render: (statut) => {
                // statut = 1 : en instance
                // statut = 2: Ouvert
                // statut = 3: Terminé
                const color = statut === '1' ? 'green' : statut === '2' ? 'orange' : 'default';
                return <Tag color={color}>{statut === '1' ? 'EN INSTANCE' : statut === '2' ? 'OUVERT' : 'TERMINE' || 'N/A'}</Tag>;
            }
        },
        {
            title: 'SAP',
            dataIndex: 'sap',
            key: 'sap',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.sap - b.sap,
            render: (value) => (
                <strong style={{ color: '#ff4d4f' }}>
                    {displayNumbers(value)} F
                </strong>
            )
        }
    ];

    // Colonnes dynamiques pour la répartition
    const beneficiaireColumns = compagniesBeneficiaires.map(comp => ({
        title: (
            <Tooltip title={comp.nom}>
                <div style={{ textAlign: 'center', fontSize: 10 }}>
                    {comp.nom}
                    {/* ({comp?.taux?.toFixed(2)}%) */}
                </div>
            </Tooltip>
        ),
        key: `benef_${comp.code}`,
        width: 80,
        align: 'right',
        render: (_, record) => {
            const rep = record.repartition?.find(r => r.beneficiaireCode === comp.code);
            return (
                <span style={{ fontSize: 11, color: '#722ed1' }}>
                    {displayNumbers(rep?.montantSAP || 0)} F
                </span>
            );
        }
    }));

    const actionColumn = {
        title: 'Action',
        key: 'action',
        width: 70,
        fixed: 'right',
        render: (_, record) => (
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onVoirDetail(record)}
            />
        )
    };

    return [...baseColumns, ...beneficiaireColumns, actionColumn];
};

export const resumeParBeneficiaireColumns = () => [
    {
        title: 'Exercice',
        dataIndex: 'exercice',
        key: 'exercice',
        width: 80,
        render: (value) => <Tag color="blue">{value}</Tag>
    },
    {
        title: 'Compagnie',
        dataIndex: 'beneficiaireNom',
        key: 'beneficiaire',
        width: 200,
        render: (nom, record) => (
            <div>
                <div style={{ fontWeight: 600 }}>{nom}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{record.beneficiaireCode}</div>
            </div>
        )
    },
    {
        title: 'Catégorie',
        dataIndex: 'categorieCode',
        key: 'categorie',
        width: 100,
        render: (code) => <Tag color="purple">{code}</Tag>
    },
    {
        title: 'Taux',
        dataIndex: 'tauxRepartition',
        key: 'taux',
        width: 80,
        align: 'center',
        render: (value) => (
            <Tag color="geekblue">{(value || 0).toFixed(2)}%</Tag>
        )
    },
    {
        title: 'Nb sinistres',
        dataIndex: 'nombreSinistres',
        key: 'nombreSinistres',
        width: 90,
        align: 'center',
        render: (value) => <strong>{value}</strong>
    },
    {
        title: 'SAP réparti',
        dataIndex: 'montantSapReparti',
        key: 'montantSap',
        width: 150,
        align: 'right',
        sorter: (a, b) => a.montantSapReparti - b.montantSapReparti,
        render: (value) => (
            <strong style={{ color: '#ff4d4f', fontSize: 14 }}>
                {displayNumbers(value)} F
            </strong>
        )
    }
];